import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { get, del } from '@utils/axios_api';
import { toast } from 'react-toastify';

const fetchCardList   = (params) => get('/bizscanpro/cards', { params });
const fetchHealth     = () => get('/bizscanpro/health', { suppressToast: true });
const fetchGroups     = () => get('/biz/groups');
const fetchGroupCards = (groupId) => get(`/biz/groups/${groupId}/cards`);

export function useBizCardList() {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [pageIndex, setPageIndex]               = useState(1);
    const [recordCountPerPage, setRecordCountPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [viewTab, setViewTab] = useState('mine'); // 'mine' | 'saved'

    const handleGroupChange = (groupId) => {
        setSelectedGroupId(groupId);
        setPageIndex(1);
        setSelectedItems([]);
    };

    const handleTabChange = (tab) => {
        setViewTab(tab);
        setPageIndex(1);
        setSelectedItems([]);
        setSelectedGroupId(null);
        setSearchParams({}, { replace: true });
        methods.reset({ q: '', status: '' });
    };

    const q      = searchParams.get('q')      || '';
    const status = searchParams.get('status') || '';

    const methods = useForm({
        defaultValues: { q, status },
        shouldFocusError: false,
    });

    useEffect(() => {
        methods.reset({ q, status });
        setPageIndex(1);
        setSelectedItems([]);
    }, [q, status]); // eslint-disable-line react-hooks/exhaustive-deps

    const serverParams = { ...(status && { status }), limit: 1000 };

    const { data: healthData, isError: isHealthError } = useQuery({
        queryKey: ['bizHealth'],
        queryFn: fetchHealth,
        staleTime: 60_000,
        retry: false,
    });

    const { data: groupsData, refetch: refetchGroups } = useQuery({
        queryKey: ['bizGroups'],
        queryFn: fetchGroups,
        staleTime: 30_000,
    });

    const { data: groupCardData } = useQuery({
        queryKey: ['bizGroupCards', selectedGroupId],
        queryFn: () => fetchGroupCards(selectedGroupId),
        enabled: selectedGroupId != null,
        staleTime: 10_000,
    });

    const groups = Array.isArray(groupsData?.result) ? groupsData.result : [];
    const groupCardIds = selectedGroupId != null && Array.isArray(groupCardData?.result)
        ? new Set(groupCardData.result)
        : null;

    const serverOk = !isHealthError && healthData?.status === 'ok';

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['bizCardList', serverParams],
        queryFn: () => fetchCardList(serverParams),
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });

    const onSubmit = (values) => {
        const newParams = new URLSearchParams();
        if (values.q?.trim()) newParams.set('q', values.q.trim());
        if (values.status)    newParams.set('status', values.status);
        setSearchParams(newParams, { replace: true });
        setPageIndex(1);
    };

    const onReset = () => {
        methods.reset({ q: '', status: '' });
        setSearchParams({}, { replace: true });
        setPageIndex(1);
    };

    const rawList = Array.isArray(data?.items) ? data.items : [];

    const term = q.trim().toLowerCase();
    const filteredList = rawList
        .filter((card) => {
            const isOwner = card.is_owner !== false;
            if (viewTab === 'mine'  && !isOwner) return false;
            if (viewTab === 'saved' && isOwner)  return false;
            if (groupCardIds != null && !groupCardIds.has(card.id)) return false;
            if (!term) return true;
            const name    = (card.person_name  || '').toLowerCase();
            const company = (card.company_name || '').toLowerCase();
            return name.includes(term) || company.includes(term);
        });

    const totalCount = filteredList.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / recordCountPerPage));
    const firstIndex = (pageIndex - 1) * recordCountPerPage;
    const cardList   = filteredList.slice(firstIndex, firstIndex + recordCountPerPage);

    const handlePageChange = (page) => {
        setPageIndex(Math.min(Math.max(1, page), totalPages));
    };

    const changePageSize = (size) => {
        setRecordCountPerPage(size);
        setPageIndex(1);
    };

    const isAllChecked = cardList.length > 0 && cardList.every((c) => selectedItems.includes(c.id));

    const handleSelectAll = () => {
        if (isAllChecked) {
            setSelectedItems((prev) => prev.filter((id) => !cardList.some((c) => c.id === id)));
        } else {
            setSelectedItems((prev) => [...new Set([...prev, ...cardList.map((c) => c.id)])]);
        }
    };

    const handleSelectItem = (id) => {
        setSelectedItems((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleUnsave = async (cardId) => {
        if (!window.confirm('받은 명함 목록에서 제거하시겠습니까?')) return;
        try {
            await del(`/biz/cards/${cardId}/save`);
            queryClient.invalidateQueries({ queryKey: ['bizCardList'] });
            toast.success('저장이 해제되었습니다.');
        } catch {
            toast.error('저장 해제 중 오류가 발생했습니다.');
        }
    };

    const handleDelete = async () => {
        if (selectedItems.length === 0) {
            toast.warn('삭제할 명함을 선택해주세요.');
            return;
        }
        if (!window.confirm(`선택한 명함 ${selectedItems.length}건을 삭제하시겠습니까?`)) return;
        const count = selectedItems.length;
        setIsDeleting(true);
        try {
            await Promise.all(selectedItems.map((id) => del(`/bizscanpro/cards/${id}`)));
            setSelectedItems([]);
            queryClient.invalidateQueries({ queryKey: ['bizCardList'] });
            toast.success(`명함 ${count}건이 삭제되었습니다.`);
        } catch {
            toast.error('삭제 중 오류가 발생했습니다.');
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        methods,
        cardList,
        totalCount,
        totalPages,
        pageIndex,
        recordCountPerPage,
        handlePageChange,
        changePageSize,
        isLoading,
        isError,
        onSubmit,
        onReset,
        refetch,
        serverOk,
        isHealthError,
        selectedItems,
        isAllChecked,
        handleSelectAll,
        handleSelectItem,
        handleDelete,
        isDeleting,
        groups,
        refetchGroups,
        selectedGroupId,
        handleGroupChange,
        viewTab,
        handleTabChange,
        handleUnsave,
    };
}
